import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getJobsStatus, queryGeminiResult } from '@/api/subtitles';
import './GeminiContent.less';
import MeetStatus, { IMeetStatus } from './MeetStatus';

// 定义生成类型枚举
export enum GeminiGenerateType {
  SUMMARY = 'SUMMARY_PROMPT',
  TRANSCRIPTION = 'TRANSCRIPTION_PROMPT',
  TRANSLATE = 'TRANSLATE_PROMPT',
}

// 毫秒 → mm:ss(逐句时间戳展示)
const fmtClock = (ms?: number) => {
  if (ms == null) return '';
  const total = Math.floor(ms / 1000);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

// 说话人头像分色:按 speaker id 里的数字取色,便于视觉区分
const SPK_COLORS = ['#7F67F5', '#5B6FE8', '#1FA971', '#E5849B', '#E5A23D', '#3DAEE5', '#B45BE8', '#E56B6B', '#4DB6AC', '#C99A2E'];
export const speakerColor = (id?: string) => {
  const m = id ? id.match(/\d+/) : null;
  let idx = 0;
  if (m) idx = Number(m[0]);
  else if (id) idx = id.length;
  return SPK_COLORS[idx % SPK_COLORS.length];
};

interface GeminiContentProps {
  isFinish: boolean;
  meetStatus: IMeetStatus;
  fileMergeId: string;
  generateType: GeminiGenerateType;
  // 整场无实时字幕（静音会议）：不轮询转录任务（新客户端零出句时压根不建任务，
  // 轮询会永远停在"生成中"），直接显示空态提示
  noSpeech?: boolean;
}

const GeminiTranscriptionContent: React.FC<GeminiContentProps> = ({
  isFinish,
  meetStatus,
  fileMergeId,
  generateType,
  noSpeech,
}) => {
  const { t } = useTranslation();
  const localMsg = useMemo(() => {
    if (generateType === GeminiGenerateType.TRANSLATE) {
      return {
        queryMsg: t('meeting.queryingTranslation'),
        generatingMsg: t('meeting.translationGenerating'),
        errorMsg: t('meeting.getTranslationFailed'),
        notFoundMsg: t('meeting.translationQueryNotFound'),
        title: t('meeting.meetingTranslation'),
      };
    }
    return {
      queryMsg: t('meeting.queryingTranscription'),
      generatingMsg: t('meeting.transcriptionGenerating'),
      errorMsg: t('meeting.getTranscriptionFailed'),
      notFoundMsg: t('meeting.transcriptionQueryNotFound'),
      title: t('meeting.meetingTranscription'),
    };
  }, [generateType, t]);

  const [loading, setLoading] = useState(true);
  const [loadMsg, setLoadMsg] = useState(localMsg.queryMsg);
  const [content, setContent] = useState<API.GeminiTranscriptionResultResponse | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const timerRef = useRef<NodeJS.Timeout>();

  const fetchGeminiResult = useCallback(async () => {
    try {
      setLoading(true);
      const { data: { data: jobsStatus } } = await getJobsStatus({
        file_merge_id: fileMergeId,
        prompt_type: generateType as 'TRANSCRIPTION_PROMPT' | 'TRANSLATE_PROMPT',
      });

      if (jobsStatus.generate_job_details.status === 'INIT') {
        // 精校翻译依赖精校转录:转录未完成时先提示"生成精校转录中",完成后再提示"翻译中"
        if (generateType === GeminiGenerateType.TRANSLATE) {
          try {
            const { data: { data: transStatus } } = await getJobsStatus({
              file_merge_id: fileMergeId,
              prompt_type: 'TRANSCRIPTION_PROMPT',
            });
            setLoadMsg(transStatus.generate_job_details.status === 'FINISH'
              ? localMsg.generatingMsg
              : t('meeting.transcriptionGenerating'));
          } catch (e) {
            setLoadMsg(localMsg.generatingMsg);
          }
        } else {
          setLoadMsg(localMsg.generatingMsg);
        }
        return;
      }

      // FAILED 是终态:停掉轮询并提示,否则落入下面的 !== 'FINISH' 分支无限转圈(与总结 job 的 P2-3 同类)。
      // 注意这里不能 setLoading(false):上面的空态分支以 !loading && 无内容 判定"未识别到语音",会抢在错误态之前渲染
      if (jobsStatus.generate_job_details.status === 'FAILED') {
        clearInterval(timerRef.current);
        setErrorMsg(localMsg.errorMsg);
        return;
      }

      if (jobsStatus.generate_job_details.status !== 'FINISH') {
        return;
      }

      clearInterval(timerRef.current);

      const { data: { data: { generate_transcription_list } } } = await queryGeminiResult({
        file_merge_id: fileMergeId,
        prompt_type: generateType as 'TRANSLATE_PROMPT' | 'TRANSCRIPTION_PROMPT',
      });

      setContent({ generate_transcription_list });
      setLoading(false);
      // 成功后清除历史错误：巡检收尾存在"merge 已 FINISH 但任务尚未创建"的短暂
      // 间隙（404），错误一旦置位若不清除会粘滞，后续轮询成功也停在错误页
      setErrorMsg('');
    } catch (error: any) {
      // 404 有两种来源，前端无法区分（响应无 owner 字段）：
      // 1) 巡检收尾的间隙——merge 已 FINISH 但任务尚未创建（几十秒内任务建好后，
      //    轮询成功会清除本错误自动恢复）
      // 2) 跨账号访问——详情走分享端点无归属校验，页面能开；转录查询是本人校验
      // 用对两种情况都准确的中性文案，避免"失败"误导
      if (error?.response?.status === 404) {
        setErrorMsg(localMsg.notFoundMsg);
        return;
      }
      console.error('Failed to fetch Gemini result:', error);
      setErrorMsg(localMsg.errorMsg);
    }
  }, [fileMergeId, generateType, localMsg, t]);

  useEffect(() => {
    if (noSpeech) return undefined;
    if (isFinish && fileMergeId) {
      timerRef.current = setInterval(fetchGeminiResult, 5000);
      fetchGeminiResult();
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        setLoading(false);
      }
    };
  }, [fileMergeId, generateType, isFinish, noSpeech, fetchGeminiResult]);

  if (!isFinish) {
    return <MeetStatus meetStatus={meetStatus} contentType="transcription" />;
  }

  // 静音会议或转录结果为空：显式空态，替代空白页
  if (noSpeech || (!loading && !content?.generate_transcription_list?.length)) {
    return (
      <div className="gemini-content">
        <div className="loading-container">
          <div className="loading-text">{t('meeting.noSpeechContent')}</div>
        </div>
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="gemini-content">
        <div className="loading-container">
          <div className="loading-text error-text">{errorMsg}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="gemini-content">
      {loading ? (
        <div className="loading-container">
          <div className="loading-text">
            <LoadingOutlined />
            {loadMsg}
          </div>
        </div>
      ) : (
        <div className="content-container">
          <div className="markdown-content-title">
            {localMsg.title}
          </div>
          <div className="markdown-content">
            {content?.generate_transcription_list?.map((item, key) => (
              <div key={key} className="transcript-row">
                <div className="speaker-column">
                  <div className="speaker-avatar" style={{ background: speakerColor(item.speaker_id) }}>
                    {item.speaker_id && item.speaker_id.length > 0
                      ? item.speaker_id.charAt(0).toUpperCase()
                      : '?'}
                  </div>
                  <span>{item.speaker_id || t('meeting.unknownSpeaker')}</span>
                  {item.begin_time != null && <time className="transcript-time">{fmtClock(item.begin_time)}</time>}
                </div>
                <div className="content-column">
                  <div className="source-text">{item.transcription}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GeminiTranscriptionContent;
